import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { IUserProps } from '../type/user-type';
import {
  getUserFromJWT,
  isLoggedIn,
  logout as oktaLogout,
  redirectPage,
  isTokenExpire,
  userLoginPage,
  IJWT,
} from '../helpers/oktaUtils';
import { handleOktaCallback } from '../api';

export interface IAuthContextProps {
  user: string;
  setUser?(...args: unknown[]): unknown;
  userData: Partial<IUserProps>;
  isAuthenticated: boolean;
  oktaUser: IJWT | undefined;
  login: () => void;
  logout: () => void;
  handleOktaLogin: (code: string) => Promise<void>;
  authLoading: boolean;
  authError: string | null;
}

const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
  children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
  const [user, setUser] = useState<string>(localStorage.getItem('aip_authUsername') || '');
  const [userData, setUserData] = useState<Partial<IUserProps>>({});
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [oktaUser, setOktaUser] = useState<IJWT | undefined>(undefined);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // 初始化时检查认证状态
  useEffect(() => {
    const checkAuth = () => {
      if (isLoggedIn()) {
        const jwtUser = getUserFromJWT();
        if (jwtUser) {
          setOktaUser(jwtUser);
          setIsAuthenticated(true);
          // 使用 JWT 中的用户名更新 user 状态
          const username = jwtUser.sub || jwtUser.name || jwtUser.email || '';
          setUser(username);
        }
      } else {
        setIsAuthenticated(false);
        setOktaUser(undefined);
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  // 保存用户名到 localStorage
  useEffect(() => {
    localStorage.setItem('aip_authUsername', user);
  }, [user]);

  // 根据 Okta 用户信息构建 userData
  useEffect(() => {
    if (user !== '' && oktaUser) {
      setUserData({
        id: oktaUser.sub,
        username: oktaUser.sub,
        name: oktaUser.name?.split(' ')[0] || oktaUser.sub,
        surname: oktaUser.name?.split(' ').slice(1).join(' ') || '',
        position: 'Okta User',
        email: oktaUser.email || '',
        src: '',
        srcSet: '',
      });
    } else {
      setUserData({});
    }
  }, [user, oktaUser]);

  // 处理 Okta 回调登录
  const handleOktaLogin = useCallback(async (code: string) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      const result = await handleOktaCallback(code);

      if (result.success && result.accessToken) {
        // 检查 token 是否有效
        if (isTokenExpire()) {
          setAuthError('Token expired');
          setIsAuthenticated(false);
          userLoginPage();
          return;
        }

        const jwtUser = getUserFromJWT();
        if (jwtUser) {
          setOktaUser(jwtUser);
          setIsAuthenticated(true);
          const username = jwtUser.sub || jwtUser.name || jwtUser.email || '';
          setUser(username);

          // 登录成功后重定向
          redirectPage();
        }
      } else {
        setAuthError(result.error || 'Login failed');
        setIsAuthenticated(false);
        userLoginPage();
      }
    } catch (error) {
      console.error('Okta login error:', error);
      setAuthError(error instanceof Error ? error.message : 'Login failed');
      setIsAuthenticated(false);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // 登录（跳转到 Okta）
  const login = useCallback(() => {
    // 这里可以选择跳转到 Okta 或者本地登录页
    window.location.href = '/auth-pages/login';
  }, []);

  // 登出
  const logout = useCallback(() => {
    setUser('');
    setOktaUser(undefined);
    setIsAuthenticated(false);
    oktaLogout();
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      userData,
      isAuthenticated,
      oktaUser,
      login,
      logout,
      handleOktaLogin,
      authLoading,
      authError,
    }),
    [
      user,
      userData,
      isAuthenticated,
      oktaUser,
      login,
      logout,
      handleOktaLogin,
      authLoading,
      authError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
